import { Phone, Mail, MapPin, MessageCircle } from "lucide-react";
import { PageHero, Reveal, SectionHead } from "../components/Reveal";
import EnquiryForm from "../components/EnquiryForm";
import { CONTACT } from "../data/site";
import useSeo from "../components/Seo";

const CARDS = [
  { icon: Phone, label: "Call Us", value: CONTACT.phoneDisplay, href: `tel:${CONTACT.phoneRaw}`, testId: "contact-call-card" },
  { icon: MessageCircle, label: "WhatsApp", value: "Chat with our team", href: `https://wa.me/${CONTACT.whatsapp}`, testId: "contact-whatsapp-card" },
  { icon: Mail, label: "Email", value: CONTACT.email, href: `mailto:${CONTACT.email}`, testId: "contact-email-card" },
  { icon: MapPin, label: "Registered Office", value: CONTACT.office, href: null, testId: "contact-office-card" },
];

export default function Contact() {
  useSeo("Contact", "Contact The Royal Mahaveer Roadlines Pvt Ltd — registered office in Doddanekkundi, Bengaluru. Call, WhatsApp or email for transportation enquiries.");
  return (
    <>
      <PageHero
        overline="Contact"
        lines={[<>Speak with</>, <>our <em className="italic text-gold-light">team</em></>]}
        desc="For transportation enquiries, route feasibility or corporate arrangements — reach us through any channel below."
      />

      <section className="py-24 sm:py-32" data-testid="contact-cards-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="grid gap-px border border-gold/15 bg-gold/15 sm:grid-cols-2 lg:grid-cols-4">
            {CARDS.map((c, i) => {
              const Icon = c.icon;
              const inner = (
                <>
                  <div className="flex items-start justify-between">
                    <Icon className="h-6 w-6 text-gold transition-colors duration-300 group-hover:text-gold-light" strokeWidth={1.5} />
                    <span className="font-heading text-sm text-gold/70">{String(i + 1).padStart(2, "0")}</span>
                  </div>
                  <p className="mt-8 text-[10px] font-semibold uppercase tracking-[0.24em] text-gold">{c.label}</p>
                  <p className="mt-3 break-words text-sm leading-[1.8] text-foreground/90">
                    {c.href?.startsWith("mailto:") ? <>{c.value.split("@")[0]}<wbr />@{c.value.split("@")[1]}</> : c.value}
                  </p>
                  <div className="mt-auto" />
                  <div className="mt-8 h-px w-10 bg-gold/25 transition-all duration-500 group-hover:w-full group-hover:bg-gold/50" />
                </>
              );
              return (
                <Reveal key={c.label} className="h-full">
                  {c.href ? (
                    <a
                      href={c.href}
                      target={c.href.startsWith("http") ? "_blank" : undefined}
                      rel="noopener noreferrer"
                      data-testid={c.testId}
                      className="group flex h-full flex-col bg-ink p-9 transition-colors duration-500 hover:bg-smoke"
                    >
                      {inner}
                    </a>
                  ) : (
                    <div data-testid={c.testId} className="group flex h-full flex-col bg-ink p-9">{inner}</div>
                  )}
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="border-t border-gold/10 py-24 sm:py-32" data-testid="contact-form-map-section">
        <div className="mx-auto grid max-w-7xl gap-16 px-6 lg:grid-cols-2 lg:px-10">
          <div>
            <SectionHead overline="Enquiry" title="Send us a message" />
            <Reveal delay={0.15} className="mt-12">
              <div className="border border-gold/15 bg-surface p-8 sm:p-10" data-testid="contact-form-panel">
                <EnquiryForm compact testIdPrefix="contact-form" />
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.2}>
            <div className="overflow-hidden border border-gold/20">
              <iframe
                title="Registered office — The Royal Mahaveer Roadlines Pvt Ltd, Doddanekkundi, Bengaluru"
                src={`https://www.google.com/maps?q=${encodeURIComponent(CONTACT.mapQuery)}&output=embed`}
                className="h-[420px] w-full grayscale invert-[0.92] contrast-[0.9] lg:h-full lg:min-h-[560px]"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="contact-map"
              />
            </div>
            <p className="mt-5 text-xs leading-relaxed text-muted-foreground">
              {CONTACT.office}
            </p>
            <p className="mt-3 text-[10px] font-semibold uppercase tracking-[0.24em] text-gold" data-testid="contact-hours">
              {CONTACT.hours} — call or WhatsApp anytime
            </p>
          </Reveal>
        </div>
      </section>
    </>
  );
}
